import React, { useEffect, useRef, useState } from "react";
import { Mic, Send, Paperclip, Square, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { processFile } from "@/utils";
import { stashTrainingSkip } from "@/lib/trainingSkip";

/**
 * Persistent AI chat bar with mic + attach + text.
 * Records audio via MediaRecorder, uploads to /api/transcribe (whisper),
 * then sends transcript to /api/ai/process which returns actions & reply.
 */
export const AIChatBar = ({
    chatLog,
    setChatLog,
    onAiResult,
    onAttach,
    onDataChanged,
    onSearchIntent,
    onLocationIntent,
    pendingTraining,
}) => {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const fileInputRef = useRef(null);
    const jsonInputRef = useRef(null);

    // ---- Smart-Filter Gatekeeper (Rule A) -------------------------------
    // Native regex checks BEFORE the AI is called, so trivial commands
    // skip the LLM entirely (saves credits, feels instant).
    const trySmartFilter = async (raw) => {
        const q = raw.trim();
        if (!q) return false;
        const low = q.toLowerCase();

        // Location intents: "I'm at X", "arrived at X", "at X now"
        const locRe = /^(?:i(?:'|)?m at|i am at|arrived (?:at|in)|at)\s+(.{2,60})(?:\s+now)?$/i;
        const locMatch = q.match(locRe);
        if (locMatch) {
            const place = locMatch[1].replace(/[.?!]+$/, "").trim();
            if (place && place.split(/\s+/).length <= 5) {
                onLocationIntent?.(place);
                setChatLog((prev) => [
                    ...prev,
                    { role: "user", text: q, ts: Date.now() },
                    { role: "assistant", text: `// scanning context for "${place}"...`, ts: Date.now() },
                ]);
                setText("");
                return true;
            }
        }

        // "note: X" / "note that X" / "scratch: X"
        const noteRe = /^(?:note(?:\s+that)?|scratch(?:pad)?)[:\s]+(.{2,})$/i;
        const noteMatch = q.match(noteRe);
        if (noteMatch) {
            const body = noteMatch[1].trim();
            const isScratch = /^scratch/i.test(q);
            try {
                if (isScratch) await api.createScratchpad({ text: body });
                else await api.createNote({ text: body });
                setChatLog((prev) => [
                    ...prev,
                    { role: "user", text: q, ts: Date.now() },
                    { role: "assistant", text: `// ${isScratch ? "scratchpad" : "note"} saved.`, ts: Date.now() },
                ]);
                toast.success(isScratch ? "Scratchpad saved" : "Note saved");
                onDataChanged?.();
            } catch (e) {
                toast.error("Save failed");
            }
            setText("");
            return true;
        }

        // Auto-search only fires for single-word inputs — those look like entity lookups
        // ("gerry", "prospera", "accountant"). Anything multi-word ("get apples", "pick up milk",
        // "call mom later") is almost always a task/note, so let the AI classify it. Users who
        // want to search a phrase can type it into the dedicated top-of-app search pill.
        const looksLikeSearch =
            /^[a-z][a-z'-]{1,24}$/i.test(q) &&
            q.split(/\s+/).length === 1 &&
            !/^(add|remind|buy|call|email|pay|book|renew|schedule|create|delete|remove|snooze|import|export|backup|restore|help|note|scratch|get|pick|grab|fetch|bring|find|fix|feed|water|wash|clean|mow|walk|run|make|cook|bake|prep|take|send|text|read|write|check|try)$/i.test(low);
        if (looksLikeSearch) {
            onSearchIntent?.(q);
            setChatLog((prev) => [
                ...prev,
                { role: "user", text: q, ts: Date.now() },
                { role: "assistant", text: `// searching for "${q}"...`, ts: Date.now() },
            ]);
            setText("");
            return true;
        }

        return false;
    };

    const doExport = async () => {
        try {
            const bundle = await api.exportBackup();
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `meyetasks-backup-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
            setChatLog((prev) => [...prev, { role: "assistant", text: "// backup exported to your downloads.", ts: Date.now() }]);
        } catch (e) {
            console.error(e);
            toast.error("Export failed");
        }
    };

    const onJsonPicked = async (e) => {
        const file = e.target.files?.[0];
        if (jsonInputRef.current) jsonInputRef.current.value = "";
        if (!file) return;
        try {
            const raw = await file.text();
            const parsed = JSON.parse(raw);
            const res = await api.importBackup(parsed); // dry run only — apply lives in Tools → Backup
            const s = res.tasks?.summary || {};
            toast.message(`Import check: ${s.safe || 0} safe · ${s.canonicalized || 0} canonicalized · ${s.blocked || 0} blocked`);
            setChatLog((prev) => [
                ...prev,
                {
                    role: "assistant",
                    text: `// import dry run: ${s.safe || 0} safe, ${s.canonicalized || 0} canonicalized, ${s.blocked || 0} blocked. nothing written — open Tools → Backup → Import to review and Apply.`,
                    ts: Date.now(),
                },
            ]);
            onDataChanged?.();
        } catch (err) {
            console.error(err);
            toast.error(err?.message?.includes("JSON") ? "Not a valid JSON file" : "Import failed");
        }
    };

    const send = async (msg) => {
        const q = (msg ?? text).trim();
        if (!q || sending) return;

        // ---- special local commands (bypass AI) ----
        const cmd = q.toLowerCase();
        if (cmd === "import" || cmd === "import backup" || cmd === "restore") {
            setText("");
            setChatLog((prev) => [...prev, { role: "user", text: q, ts: Date.now() }]);
            jsonInputRef.current?.click();
            return;
        }
        if (cmd === "export" || cmd === "export backup" || cmd === "backup") {
            setText("");
            setChatLog((prev) => [...prev, { role: "user", text: q, ts: Date.now() }]);
            await doExport();
            return;
        }
        if (cmd === "help" || cmd === "?") {
            setText("");
            setChatLog((prev) => [
                ...prev,
                { role: "user", text: q, ts: Date.now() },
                {
                    role: "assistant",
                    text:
                        "// commands: import · export · search {word} · 'I'm at {place}' · 'note: {text}' · or ask me anything.",
                    ts: Date.now(),
                },
            ]);
            return;
        }

        // ---- Smart-Filter Gatekeeper: skip AI for search / location / notes ----
        // (only when there's no pending training — an answer must always reach the AI)
        if (!pendingTraining && (await trySmartFilter(q))) return;

        setSending(true);
        const userMsg = { role: "user", text: q, ts: Date.now() };
        setChatLog((prev) => [...prev, userMsg]);
        setText("");
        try {
            const res = await api.aiProcess(q, [...chatLog, userMsg].slice(-8));
            const aiMsg = {
                role: "assistant",
                text: res.reply || "",
                training_question: res.training_question,
                ts: Date.now(),
            };
            setChatLog((prev) => [...prev, aiMsg]);
            onAiResult?.(res);
            if (res.created?.length) {
                const kinds = res.created.map((c) => c.kind);
                const summary = kinds.reduce((acc, k) => ({ ...acc, [k]: (acc[k] || 0) + 1 }), {});
                const parts = Object.entries(summary).map(([k, v]) => `${v} ${k}${v > 1 ? "s" : ""}`);
                if (parts.length) toast.success(`Added ${parts.join(", ")}`);
            }
        } catch (e) {
            console.error(e);
            toast.error("AI failed. Try again.");
        } finally {
            setSending(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setTranscribing(true);
                try {
                    const { text: transcript } = await api.transcribe(blob);
                    if (transcript?.trim()) {
                        await send(transcript);
                    } else {
                        toast.warning("Nothing transcribed");
                    }
                } catch (e) {
                    console.error(e);
                    toast.error("Transcription failed");
                } finally {
                    setTranscribing(false);
                }
            };
            rec.start();
            recorderRef.current = rec;
            setRecording(true);
        } catch (e) {
            console.error(e);
            toast.error("Mic access denied");
        }
    };

    const stopRecording = () => {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
        setRecording(false);
    };

    const onFilePick = async (e) => {
        const files = Array.from(e.target.files || []);
        for (const f of files) {
            try {
                const p = await processFile(f);
                onAttach?.(p);
            } catch (err) {
                toast.error(err.message || "File error");
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    useEffect(() => () => stopRecording(), []);

    return (
        <div
            className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/70 border-t border-meye-cyan/25"
            data-testid="ai-chat-bar"
        >
            <div className="max-w-2xl mx-auto px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                {pendingTraining?.question && (
                    <div
                        className="mb-2 rounded-lg border border-meye-fuchsia/50 bg-meye-fuchsia/10 px-3 py-2.5 fade-up"
                        data-testid="pending-training-banner"
                    >
                        <div className="flex items-start gap-2">
                            <span className="text-meye-fuchsia font-mono text-[10px] uppercase tracking-widest mt-0.5">?</span>
                            <p className="text-sm text-meye-fuchsia font-mono flex-1 leading-snug min-w-0" style={{ overflowWrap: "anywhere" }}>
                                {pendingTraining.question}
                            </p>
                        </div>
                        {/* Full-width action row — big pill buttons with clear labels so no one has
                            to hunt for a tiny X. Both buttons stash a 7-day cooldown (keyed by
                            training_context AND the normalised question text) — see App.js
                            `loadAll` + `onAiResult` for the auto-re-clear logic. */}
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs font-brand font-bold uppercase tracking-widest">
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        stashTrainingSkip(pendingTraining);
                                        await api.updateSettings({ pending_training: null });
                                        onDataChanged?.();
                                    } catch (e) { console.error(e); }
                                }}
                                className="py-2 rounded-md border border-meye-fuchsia/40 text-meye-fuchsia/90 hover:text-white hover:bg-meye-fuchsia/25 hover:border-meye-fuchsia active:scale-95 transition-all"
                                data-testid="pending-training-skip"
                            >
                                Not now
                            </button>
                            <button
                                type="button"
                                onClick={async () => {
                                    try {
                                        stashTrainingSkip(pendingTraining);
                                        await api.updateSettings({ pending_training: null });
                                        onDataChanged?.();
                                    } catch (e) { console.error(e); }
                                }}
                                className="py-2 rounded-md bg-meye-fuchsia/80 text-white hover:bg-meye-fuchsia active:scale-95 transition-all inline-flex items-center justify-center gap-1.5"
                                data-testid="pending-training-dismiss"
                                aria-label="Dismiss question"
                            >
                                <X size={13} strokeWidth={3} /> Dismiss
                            </button>
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={onFilePick}
                        className="hidden"
                        data-testid="chat-file-input"
                    />
                    <input
                        ref={jsonInputRef}
                        type="file"
                        accept="application/json,.json"
                        onChange={onJsonPicked}
                        className="hidden"
                        data-testid="chat-json-import-input"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-zinc-400 hover:text-meye-cyan transition-colors"
                        title="Attach"
                        data-testid="chat-attach-btn"
                    >
                        <Paperclip size={18} strokeWidth={1.5} />
                    </button>

                    <div className="flex-1 relative">
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            placeholder={
                                sending
                                    ? "thinking..."
                                    : pendingTraining?.question
                                      ? "type your answer..."
                                      : "ask the eye · 'I'm at X' · 'note: ...' · 'import'"
                            }
                            disabled={sending || transcribing}
                            className="w-full bg-black/60 border border-meye-border rounded-md px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-meye-cyan/70 focus:ring-1 focus:ring-meye-cyan/30 transition-colors font-mono"
                            data-testid="chat-input"
                        />
                        {transcribing && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-meye-cyan">
                                <Loader2 size={12} className="animate-spin" /> transcribing
                            </div>
                        )}
                    </div>

                    {recording ? (
                        <button
                            onClick={stopRecording}
                            className="p-2.5 rounded-full bg-meye-fuchsia text-black mic-pulse"
                            title="Stop"
                            data-testid="chat-stop-recording-btn"
                        >
                            <Square size={16} strokeWidth={2} fill="currentColor" />
                        </button>
                    ) : (
                        <button
                            onClick={startRecording}
                            disabled={sending || transcribing}
                            className="p-2.5 rounded-full border border-meye-fuchsia/50 text-meye-fuchsia hover:bg-meye-fuchsia/10 transition-colors disabled:opacity-40"
                            title="Voice"
                            data-testid="chat-mic-btn"
                        >
                            <Mic size={16} strokeWidth={1.75} />
                        </button>
                    )}

                    <button
                        onClick={() => send()}
                        disabled={!text.trim() || sending}
                        className="p-2.5 rounded-full bg-meye-cyan text-black hover:bg-cyan-300 transition-colors disabled:opacity-40"
                        title="Send"
                        data-testid="chat-send-btn"
                    >
                        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AIChatBar;
